import { useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { ApiError } from "@/lib/apiClient";
import {
  getDuplicateNummerStatus,
  renumberDuplicateNummer,
  type DuplicateNummerStatus,
} from "@/lib/projectOverview";
import { toast } from "@/lib/toast";

export function DuplicateNummerButton({
  projectId,
  onDone,
}: {
  projectId: number;
  onDone?: () => void;
}) {
  const [info, setInfo] = useState<DuplicateNummerStatus | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setInfo(null);
    void getDuplicateNummerStatus(projectId)
      .then((data) => {
        if (!cancelled) setInfo(data.eligible ? data : null);
      })
      .catch(() => {
        if (!cancelled) setInfo(null);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  if (!info?.eligible) return null;

  async function handleConfirm() {
    setBusy(true);
    try {
      const result = await renumberDuplicateNummer(projectId);
      toast.success(
        `Neue Nummer ${result.neue_auftragsnummer}. Der Verweis steht in den Notizen von ${result.keeper_auftragsnummer}.`,
      );
      setOpen(false);
      setInfo(null);
      onDone?.();
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Umnummerieren fehlgeschlagen.";
      toast.error(message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Button type="button" size="sm" variant="secondary" onClick={() => setOpen(true)}>
        Neue Nummer vergeben
      </Button>
      <Dialog
        open={open}
        onClose={() => {
          if (!busy) setOpen(false);
        }}
        title="Neue Projektnummer vergeben"
        description={`Dieses Projekt trägt die doppelte Nummer ${info.shared_auftragsnummer} und liegt im falschen Ordner.`}
        footer={
          <>
            <Button type="button" variant="secondary" disabled={busy} onClick={() => setOpen(false)}>
              Abbrechen
            </Button>
            <Button type="button" loading={busy} onClick={() => void handleConfirm()}>
              Nummer vergeben
            </Button>
          </>
        }
      >
        <div className="space-y-2 text-sm text-label-secondary">
          <p>
            Es bekommt die nächste freie Nummer im Jahr {info.target_jahr}
            {info.naechste_auftragsnummer ? ` (voraussichtlich ${info.naechste_auftragsnummer})` : ""}{" "}
            und wird in den Ordner {info.keeper_ordner_label} verschoben.
          </p>
          <p>
            {info.keeper_auftragsnummer} behält seine Nummer. In den Projektnotizen dieses Projekts
            wird ein Verweis auf die neue Nummer angelegt.
          </p>
        </div>
      </Dialog>
    </>
  );
}
